/**
 * The log composer's pure half (13-26): which rows a register's handling verb may add, and how the composer's form
 * becomes 13-20's `SiblingInput`. `components/record/SiblingComposer.tsx` renders it; `buildRecordVerb('handling',
 * …)` decides the save and screens the text (contact details, cap, controls: §1.6, D-13-20-9).
 *
 * The kinds come from FIELDSET §3.4.1 as 13-17 typed it (`ENTRY_TYPE_MAP`, `LOG_KIND_MAP`): every row whose writers
 * include the handling verb's code (`HND`) on this register, in the map's order. Nothing is invented and nothing is
 * retyped: the labels are 13-25's (`ENTRY_TYPE_LABELS`, `LOG_KIND_LABELS`). Rows other writers own are not offered
 * here: `Review` is the Risk review's (CLO), `Notification` the notification verb's, `SitePreservation` / `RTW` the
 * WHS verb's.
 */

import { ENTRY_TYPE_MAP, LOG_KIND_MAP } from '../config/fieldset/siblings';
import type { AuditEntryType } from '../types/qms';
import type { RecordRegister, Refusal } from '../types/records';
import type { SiblingWriter } from '../config/fieldset/siblings';
import { domainOf } from './lodgeLayout';
import { ENTRY_TYPE_LABELS, LOG_KIND_LABELS } from './recordTimeline';
import { WRITER_OF_VERB } from './recordVerbs';
import type { SiblingInput } from './recordVerbs';

/** The writer code the composer saves under: the handling verb's (FIELDSET §4.0 `HND`). */
export const COMPOSER_WRITER = WRITER_OF_VERB.handling;

const NOTE: AuditEntryType = 'Note';
const INVESTIGATION_STEP: AuditEntryType = 'InvestigationStep';

/** One kind of row the composer offers. `key` is the `LogKind` for a `Note` row, else the `EntryType`. */
export interface ComposerKind {
  key: string;
  entryType: AuditEntryType;
  logKind?: string;
  label: string;
}

/**
 * The rows a verb may add on this register, in §3.4.1's order: the two entry types, then the notes. Default: the
 * handling verb's (`HND`); 13-27's notification panel asks for the notification verb's (`NOT`).
 */
export function composerKinds(register: RecordRegister, writer: SiblingWriter = COMPOSER_WRITER): ComposerKind[] {
  const out: ComposerKind[] = [];
  for (const [entryType, use] of Object.entries(ENTRY_TYPE_MAP)) {
    if (!use.registers.includes(register) || !use.writers.includes(writer)) continue;
    out.push({ key: entryType, entryType: entryType as AuditEntryType, label: ENTRY_TYPE_LABELS[entryType as AuditEntryType] ?? entryType });
  }
  for (const [logKind, use] of Object.entries(LOG_KIND_MAP)) {
    if (!use.registers.includes(register) || !use.writers.includes(writer)) continue;
    out.push({ key: logKind, entryType: NOTE, logKind, label: LOG_KIND_LABELS[logKind] ?? logKind });
  }
  return out;
}

export function isInvestigationStep(kind: ComposerKind | undefined): boolean {
  return kind?.entryType === INVESTIGATION_STEP;
}

/** What the composer's inputs hold. `day` / `time` are when it happened (Melbourne); `today` is Melbourne today. */
export interface ComposerForm {
  kind: ComposerKind;
  text: string;
  day: string;
  time: string;
  investigator: string;
  position: string;
}

/**
 * The form as one `SiblingInput`. A row dated today with no time is left undated (the engine dates it at save).
 * An investigation step carries the investigator (`actorUpn`, default the person) and their position.
 */
export function composerRow(form: ComposerForm, today: string, actorUpn: string): SiblingInput {
  const row: SiblingInput = { entryType: form.kind.entryType, text: form.text };
  if (form.kind.logKind) row.logKind = form.kind.logKind;
  const day = form.day.trim();
  const time = form.time.trim();
  if (day !== '' && (day !== today || time !== '')) row.at = time ? { day, time } : { day };
  if (isInvestigationStep(form.kind)) {
    row.actorUpn = form.investigator.trim() || actorUpn;
    if (form.position.trim() !== '') row.position = form.position;
  }
  return row;
}

/** Which composer input an engine refusal belongs to (the engine places every row refusal at `rows.0`). */
export type ComposerInput = 'kind' | 'text' | 'when' | 'investigator' | 'position';

export function composerInputOf(code: string): ComposerInput {
  switch (code) {
    case 'invalid-upn':
    case 'guest-upn':
    case 'not-harmony-address':
      return 'investigator';
    case 'position-not-person':
      return 'position';
    case 'invalid-date':
    case 'invalid-day':
    case 'invalid-time':
    case 'nonexistent-time':
    case 'in-future':
      return 'when';
    case 'logkind-required':
    case 'logkind-not-allowed':
    case 'entrytype-not-allowed':
      return 'kind';
    default:
      return 'text';
  }
}

/**
 * The composer's own check before the engine: an investigator is a Harmony address, read as the signed-in person's
 * own domain (D-13-24-5 / D-13-25-13). The engine still checks the shape.
 */
export function composerRefusals(form: ComposerForm, actorUpn: string): Refusal[] {
  if (!isInvestigationStep(form.kind)) return [];
  const value = form.investigator.trim();
  const house = domainOf(actorUpn);
  if (value === '' || !house) return [];
  const domain = domainOf(value);
  return domain && domain !== house
    ? [{ code: 'not-harmony-address', column: 'investigator', message: 'The investigator: use their Harmony email address.' }]
    : [];
}

// ---------------------------------------------------------------------------
// 13-28: prompted rows (the WHS panel's site-preservation entry, FIELDSET §3.4.1 G-179)
// ---------------------------------------------------------------------------

/**
 * A question the composer asks before the free text. Its answer is written at the head of the row's `Note` as
 * `<label> <answer>`, one per line: a sibling row has no columns of its own (FIELDSET §1.7), so the structure is in
 * the text. `choices` are the obligation row's own words; nothing is pre-selected. A required prompt refuses a blank.
 */
export interface ComposerPrompt {
  key: string;
  label: string;
  choices?: readonly string[];
  required?: boolean;
  hint?: string;
}

/** Each required prompt left blank, as `{ key, message }`. */
export function promptRefusals(prompts: readonly ComposerPrompt[], answers: Readonly<Record<string, string>>): { key: string; message: string }[] {
  return prompts
    .filter((p) => p.required && (answers[p.key] ?? '').trim() === '')
    .map((p) => ({ key: p.key, message: `Answer “${p.label}” first.` }));
}

/** The row's text: the answered prompts, one per line, then what the person wrote. */
export function promptedText(prompts: readonly ComposerPrompt[], answers: Readonly<Record<string, string>>, text: string): string {
  const lines = prompts
    .map((p) => [p.label, (answers[p.key] ?? '').trim()] as const)
    .filter(([, a]) => a !== '')
    .map(([label, a]) => `${label} ${a}`);
  return [...lines, text.trim()].filter((l) => l !== '').join('\n');
}
